import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://readybuyergroup.com.au';

const agentsSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/agents#webpage`,
      url: `${SITE_URL}/agents`,
      name: 'Real Estate Agent Partnerships Melbourne | ReadyBuyer Group | Fast Buyer',
      description:
        'Partner with ReadyBuyer Group and give your motivated Melbourne vendors a fast, certain outcome. Finance approved, zero fees, off-market and on-market properties welcome.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      inLanguage: 'en-AU',
    },
    {
      '@type': 'Service',
      '@id': `${SITE_URL}/agents#service`,
      name: 'Real Estate Agent Partnership Program',
      provider: {
        '@type': 'RealEstateAgent',
        name: 'ReadyBuyer Group',
        url: SITE_URL,
      },
      description:
        'ReadyBuyer Group partners with Melbourne real estate agents to provide a fast, finance-approved buyer for motivated vendors. Off-market, on-market, and pre-market properties all welcome. Zero fees charged to agents.',
      areaServed: {
        '@type': 'City',
        name: 'Melbourne',
      },
      serviceType: 'Real Estate Agent Partnership',
    },
    {
      '@type': 'HowTo',
      name: 'How to Partner with ReadyBuyer Group as a Real Estate Agent',
      description: 'Simple four-step process for real estate agents to submit properties and close deals faster with ReadyBuyer Group.',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Submit the property',
          text: 'Share the property details with us — off-market, on-market, or pre-market. Include vendor motivation and price expectations.',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'We assess it',
          text: 'We review against our buying criteria and let you know quickly if it fits our brief.',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'We inspect & offer',
          text: 'If it fits, we arrange an inspection and deliver a written offer within 3-5 business days.',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Deal closes',
          text: 'We move to contract quickly. Your vendor gets certainty. You close the deal without the usual drawn-out process.',
        },
      ],
    },
  ],
};

export default function AgentsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    document.title = 'Real Estate Agent Partnerships Melbourne | ReadyBuyer Group | Fast Buyer';
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', 'Partner with ReadyBuyer Group and give your motivated Melbourne vendors a fast, certain outcome. Finance approved, zero fees, off-market and on-market properties welcome.');

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-agents';
    script.textContent = JSON.stringify(agentsSchema);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (descEl) descEl.setAttribute('content', prevDesc);
      document.getElementById('schema-agents')?.remove();
    };
  }, []);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4644b4f740dbaf47e558cadac9d2962f.jpg"
            alt="Real estate agent"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-950/70 to-gray-950/30"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-5">For Real Estate Agents</p>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6 max-w-2xl">
            A buyer who<br />closes quickly.
          </h1>
          <p className="text-gray-300 text-xl leading-relaxed mb-10 max-w-xl">
            Partner with ReadyBuyer Group and give your motivated vendors a fast, certain outcome — before or after the property hits the market.
          </p>
          <button
            onClick={scrollToContact}
            className="px-8 py-4 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap text-base inline-flex items-center gap-2"
          >
            Submit a Property
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Why partner with us */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-5">Why Partner With Us</p>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-950 leading-tight mb-8">
                We don&apos;t drag<br />out the process.
              </h2>
              <p className="text-gray-500 text-xl leading-relaxed mb-10">
                We&apos;re finance approved, charge zero fees, and can give your motivated vendors a fast, certain outcome. Present us as a reliable buyer who closes — and close more deals.
              </p>
              <div className="space-y-4">
                {[
                  'No fees charged to you — ever',
                  'Finance approved and ready to move immediately',
                  'Off-market, on-market, pre-market — all welcome',
                  'Distressed sales, renovations, subdivisions, confidential sales',
                  'We assess quickly and make clean, decisive offers',
                  'Your vendor gets certainty — you close the deal faster',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-base text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-lg"></i>
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Two%20professionals%20shaking%20hands%20in%20front%20of%20a%20Melbourne%20property%2C%20business%20partnership%2C%20warm%20natural%20light%2C%20modern%20suburban%20street%2C%20confident%20and%20friendly%2C%20Australian%20real%20estate%20context&width=700&height=600&seq=agents-partner-01&orientation=portrait"
                alt="Partnership"
                className="w-full h-[460px] object-cover object-top rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What we're looking for */}
      <section className="py-20 md:py-28 bg-gray-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-5">Our Buying Criteria</p>
              <h2 className="text-4xl md:text-5xl font-extrabold leading-tight mb-8">
                What we&apos;re<br />looking for
              </h2>
              <p className="text-gray-400 text-xl leading-relaxed mb-10">
                We buy residential properties across Melbourne with value-add potential. Here&apos;s what gets our attention — reach out and ask us for our full buying brief.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-home-4-line', label: 'Houses & townhouses' },
                  { icon: 'ri-building-line', label: 'Units & apartments' },
                  { icon: 'ri-tools-line', label: 'Renovation potential' },
                  { icon: 'ri-layout-grid-line', label: 'Subdivision opportunities' },
                  { icon: 'ri-auction-line', label: 'Distressed sales' },
                  { icon: 'ri-lock-line', label: 'Confidential sales' },
                  { icon: 'ri-eye-off-line', label: 'Off-market listings' },
                  { icon: 'ri-file-list-3-line', label: 'Deceased estates' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="w-9 h-9 flex items-center justify-center bg-teal-700/30 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-teal-400 text-base`}></i>
                    </div>
                    <span className="text-gray-200 text-base font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-6">
                <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-6">When you submit a property, include:</p>
                <ul className="space-y-5">
                  {[
                    { icon: 'ri-route-line', title: 'Your process for buyers like us', desc: 'How do you prefer to work? What does your vendor need?' },
                    { icon: 'ri-heart-pulse-line', title: 'Vendor motivation', desc: "Why are they selling? How motivated are they? What's their timeline?" },
                    { icon: 'ri-price-tag-3-line', title: 'Price expectations', desc: 'Price guide, reserve, or asking price — whatever you can share.' },
                    { icon: 'ri-information-line', title: 'Property details', desc: 'Any constraints, tenants, access issues, or relevant history.' },
                  ].map((item) => (
                    <li key={item.title} className="flex gap-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-teal-700/30 rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-teal-400 text-base`}></i>
                      </div>
                      <div>
                        <div className="text-white font-semibold text-base mb-1">{item.title}</div>
                        <div className="text-gray-400 text-sm leading-relaxed">{item.desc}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-teal-700/20 border border-teal-600/30 rounded-2xl p-6">
                <p className="text-teal-300 text-sm font-semibold uppercase tracking-wide mb-2">Our promise to you</p>
                <p className="text-gray-300 text-base leading-relaxed">
                  We assess every submission seriously and respond quickly. If it fits our brief, we move fast. If it doesn&apos;t, we&apos;ll tell you why — so you know exactly where you stand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works for agents */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-4">The Process</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-950 leading-tight mb-6">
              Simple. Fast. Decisive.
            </h2>
            <p className="text-gray-500 text-xl max-w-xl mx-auto leading-relaxed">
              From submission to offer — here&apos;s how working with us looks.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
            {[
              { num: '01', title: 'Submit the property', desc: 'Share the property details with us — off-market, on-market, or pre-market. Include vendor motivation and price expectations.', tag: 'Day 1' },
              { num: '02', title: 'We assess it', desc: 'We review against our buying criteria and let you know quickly if it fits our brief. No long waits, no vague responses.', tag: 'Days 1–2' },
              { num: '03', title: 'We inspect & offer', desc: 'If it fits, we arrange an inspection and deliver a written offer within 3–5 business days. Finance is already approved.', tag: 'Days 3–5' },
              { num: '04', title: 'Deal closes', desc: 'We move to contract quickly. Your vendor gets certainty. You close the deal without the usual drawn-out process.', tag: 'Fast settlement' },
            ].map((step, i) => (
              <div key={step.num} className={`p-8 ${i < 3 ? 'border-r border-gray-100' : ''} border-t border-gray-100`}>
                <div className="text-5xl font-extrabold text-gray-100 mb-4">{step.num}</div>
                <div className="inline-block text-xs font-semibold text-teal-700 bg-teal-50 px-3 py-1 rounded-full mb-4 whitespace-nowrap">{step.tag}</div>
                <h3 className="text-lg font-bold text-gray-950 mb-3">{step.title}</h3>
                <p className="text-gray-500 text-base leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F4F9F8] text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-950 mb-6">Ready to submit a property?</h2>
          <p className="text-gray-500 text-xl leading-relaxed mb-10">
            We respond quickly, move decisively, and close deals. Let&apos;s talk.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="px-10 py-4 bg-gray-950 hover:bg-teal-700 text-white font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap text-base inline-flex items-center justify-center gap-2"
            >
              Submit a Property
              <i className="ri-arrow-right-line"></i>
            </button>
            <a
              href="mailto:info@readybuyergroup.com.au"
              rel="nofollow"
              className="px-10 py-4 border border-gray-300 text-gray-700 font-semibold rounded-full hover:border-teal-600 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap text-base inline-flex items-center justify-center gap-2"
            >
              Email Us Directly
              <i className="ri-mail-line"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
